import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Cta, FAQS } from "@/pages/Home";
import { EMAIL, PHONE, PHONE_TEL } from "@/lib/fda";
import { LEGAL_DISCLOSURE } from "@/lib/legal";

const Shell = ({ title, lead, children, testid }) => (
  <div className="px-5 py-16 lg:px-10" data-testid={testid}>
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-4xl font-extrabold text-forest sm:text-5xl">{title}</h1>
      {lead && <p className="mt-5 text-base font-semibold text-forest-soft md:text-lg">{lead}</p>}
      <div className="mt-10 space-y-5 text-sm leading-relaxed text-forest-soft">{children}</div>
      <div className="mt-12"><Cta id={`${testid}-cta`} /></div>
    </div>
  </div>
);

export function HowItWorks() {
  return (
    <Shell testid="how-it-works-page" title="How it works" lead="Three simple steps, no obligation at any point.">
      <ol className="space-y-8">
        {[["Answer a few questions", "Our short assessment asks about your debts, your income and your circumstances. It is one question per screen and takes a few minutes. It is not a credit application."],
          ["Speak to our team", "Your answers arrive with the Freedom Debt Advice team along with a reference number. We review what you have told us and may contact you at the time you asked us to."],
          ["Understand your options", "If it is appropriate, we introduce you to a relevant FCA-regulated partner who can give you advice. We never decide that a particular debt solution is suitable for you."]].map(([t, b], i) => (
          <li key={t}>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta">Step {i + 1}</p>
            <h2 className="mt-2 font-display text-xl font-bold text-forest">{t}</h2>
            <p className="mt-2">{b}</p>
          </li>
        ))}
      </ol>
      <p className="rounded-xl border border-border bg-white p-5">{LEGAL_DISCLOSURE}</p>
    </Shell>
  );
}

export function DebtOptions() {
  return (
    <Shell testid="debt-options-page" title="Debt options" lead="General information only. Nothing on this page is advice or a recommendation.">
      <p>There are a number of different ways people deal with problem debt in the UK. Which of them, if any,
        is appropriate depends entirely on your individual circumstances, and only an FCA-regulated adviser can
        tell you that. The summaries below are general information so you know what the terms mean.</p>
      {[["Debt Management Plan (DMP)", "An informal arrangement where you make one monthly payment towards your unsecured debts, usually managed by a third party."],
        ["Individual Voluntary Arrangement (IVA)", "A formal, legally binding agreement between you and your creditors, available in England, Wales and Northern Ireland."],
        ["Debt Relief Order (DRO)", "A formal option for people with lower levels of debt, low income and few assets, subject to strict eligibility criteria."],
        ["Bankruptcy", "A formal insolvency procedure with significant consequences that should only be considered with regulated advice."],
        ["Trust Deed / Debt Arrangement Scheme", "Scottish equivalents of some of the arrangements above, with their own rules."],
        ["Talking to your creditors directly", "Many creditors have hardship processes and it is always free to speak to them yourself."]].map(([t, b]) => (
        <div key={t} className="rounded-xl border border-border bg-white p-5">
          <h2 className="font-display text-lg font-bold text-forest">{t}</h2>
          <p className="mt-2">{b}</p>
        </div>
      ))}
      <p className="font-semibold text-forest">Freedom Debt Advice does not tell you that you qualify for, or have been
        approved for, any of the above. We are not authorised and regulated by the Financial Conduct Authority.</p>
    </Shell>
  );
}

export function Faqs() {
  return (
    <div className="px-5 py-16 lg:px-10" data-testid="faqs-page">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-4xl font-extrabold text-forest sm:text-5xl">FAQs</h1>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`} data-testid={`faqs-page-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-forest">{q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-forest-soft">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12"><Cta id="faqs-cta" /></div>
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="px-5 py-16 lg:px-10" data-testid="contact-page">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-4xl font-extrabold text-forest sm:text-5xl">Contact Freedom Debt Advice</h1>
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="rounded-2xl border border-border bg-white p-6 transition-transform hover:-translate-y-1">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta">Telephone</p>
            <p className="mt-2 font-display text-2xl font-bold text-forest">{PHONE}</p>
          </a>
          <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="rounded-2xl border border-border bg-white p-6 transition-transform hover:-translate-y-1">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta">Email</p>
            <p className="mt-2 break-all font-display text-lg font-bold text-forest">{EMAIL}</p>
          </a>
        </div>
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 text-sm leading-relaxed text-forest-soft">
          <p className="font-semibold text-forest">Freedom Debt Advice</p>
          <p className="mt-2">A trading name of RJA Solutions Ltd</p>
          <p>Company number 14390777</p>
          <p>Registered office: 20 Stephen Street, Urmston, Manchester, England, M41 9AT</p>
          <p>ICO Fee Payer Register Number: ZC126432</p>
          <p>Governing law: England and Wales</p>
        </div>
        <div className="mt-12"><Cta id="contact-cta" /></div>
      </div>
    </div>
  );
}
